import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ClipboardCheck, History, Inbox, Search } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { PageTransition } from '../components/PageTransition';
import { EmptyState } from '../components/shared/empty-state';
import { LeaveApprovalRow } from '../components/leave/LeaveApprovalRow';
import RejectReasonDialog from '../components/RejectReasonDialog';
import {
  leaveRequestService,
  LeaveRequest,
} from '../services/leaveRequestService';
import { useNotification } from '../contexts/NotificationContext';
import { useApprovalCounts } from '../contexts/ApprovalCountsContext';
import { ApprovalScopeToggle } from '../components/approvals/ApprovalScopeToggle';
import { useApprovalScope } from '../hooks/use-approval-scope';

type HistoryState = 'all' | 'approved' | 'rejected' | 'cancelled';

const RowSkeleton: React.FC = () => (
  <div className="flex items-center gap-3 px-3 py-2.5">
    <Skeleton className="h-8 w-8 rounded-md" />
    <div className="flex-1 space-y-1.5">
      <Skeleton className="h-4 w-40" />
      <Skeleton className="h-3 w-56" />
    </div>
    <Skeleton className="h-8 w-24" />
  </div>
);

const RowListSkeleton: React.FC = () => (
  <Card className="divide-y">
    {[1, 2, 3, 4].map((i) => (
      <RowSkeleton key={i} />
    ))}
  </Card>
);

const PendingLeaveRequests: React.FC = () => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const { refreshCounts } = useApprovalCounts();

  const [requests, setRequests] = useState<LeaveRequest[]>([]);
  const [history, setHistory] = useState<LeaveRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [requestToReject, setRequestToReject] = useState<LeaveRequest | null>(null);
  const [search, setSearch] = useState('');
  const [historyState, setHistoryState] = useState<HistoryState>('all');
  const scope = useApprovalScope();

  const fetchPending = useCallback(async () => {
    if (!scope.ready) return;
    try {
      setLoading(true);
      const data = await leaveRequestService.getPending(scope.onlyMine);
      setRequests(data);
    } catch (error) {
      console.error('Error fetching pending leave requests:', error);
    } finally {
      setLoading(false);
    }
  }, [scope.ready, scope.onlyMine]);

  const fetchHistory = useCallback(async () => {
    if (!scope.ready) return;
    try {
      setHistoryLoading(true);
      const data = await leaveRequestService.getHistory(scope.onlyMine);
      setHistory(data);
    } catch (error) {
      console.error('Error fetching leave request history:', error);
    } finally {
      setHistoryLoading(false);
    }
  }, [scope.ready, scope.onlyMine]);

  useEffect(() => {
    fetchPending();
    fetchHistory();
  }, [fetchPending, fetchHistory]);

  const matchesSearch = useCallback(
    (request: LeaveRequest) => {
      const term = search.trim().toLowerCase();
      if (!term) return true;
      return (
        request.user?.name?.toLowerCase().includes(term) ||
        request.user?.email?.toLowerCase().includes(term) ||
        false
      );
    },
    [search],
  );

  const visiblePending = useMemo(() => requests.filter(matchesSearch), [requests, matchesSearch]);

  const visibleHistory = useMemo(
    () =>
      history.filter(
        (request) => matchesSearch(request) && (historyState === 'all' || request.state === historyState),
      ),
    [history, matchesSearch, historyState],
  );

  const handleApprove = async (request: LeaveRequest) => {
    try {
      setProcessing(true);
      await leaveRequestService.approve(request.id);
      showNotification(t('leave.messages.approved', 'Leave request approved'), 'success');
      await Promise.all([fetchPending(), fetchHistory()]);
      await refreshCounts();
    } catch (error) {
      console.error('Error approving leave request:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectClick = (request: LeaveRequest) => {
    setRequestToReject(request);
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async (reason: string) => {
    if (!requestToReject) return;

    try {
      setProcessing(true);
      await leaveRequestService.reject(requestToReject.id, reason);
      showNotification(t('leave.messages.rejected', 'Leave request rejected'), 'success');
      setRejectDialogOpen(false);
      setRequestToReject(null);
      await Promise.all([fetchPending(), fetchHistory()]);
      await refreshCounts();
    } catch (error) {
      console.error('Error rejecting leave request:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectDialogClose = () => {
    if (!processing) {
      setRejectDialogOpen(false);
      setRequestToReject(null);
    }
  };

  return (
    <PageTransition className="p-4 sm:p-6 space-y-4">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
            <ClipboardCheck className="h-5 w-5 text-orange-600 dark:text-orange-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              {t('leave.pending.title', 'Pending Leave Approvals')}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t('leave.pending.subtitle', '{{count}} pending requests', { count: requests.length })}
            </p>
          </div>
        </div>
        <ApprovalScopeToggle scope={scope} />
      </motion.div>

      <Tabs defaultValue="pending" className="space-y-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <TabsList>
            <TabsTrigger value="pending" className="gap-2">
              <Inbox className="h-4 w-4" />
              {t('leave.pending.tabs.pending', 'Da approvare')}
              {requests.length > 0 && (
                <span className="rounded-full bg-orange-100 px-1.5 text-[11px] font-semibold tabular-nums text-orange-700 dark:bg-orange-900/40 dark:text-orange-300">
                  {requests.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="history" className="gap-2">
              <History className="h-4 w-4" />
              {t('leave.pending.tabs.history', 'Storico')}
            </TabsTrigger>
          </TabsList>

          <div className="relative sm:w-64">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('leave.pending.searchPlaceholder', 'Cerca persona…')}
              className="h-9 pl-8"
            />
          </div>
        </div>

        {/* Pending */}
        <TabsContent value="pending" className="mt-0">
          {loading ? (
            <RowListSkeleton />
          ) : visiblePending.length === 0 ? (
            <Card>
              <EmptyState
                icon={Inbox}
                title={t('leave.pending.empty.title', 'No pending requests')}
                description={t(
                  'leave.pending.empty.description',
                  'There are no leave requests waiting for your approval.',
                )}
              />
            </Card>
          ) : (
            <Card className="divide-y overflow-hidden">
              {visiblePending.map((request) => (
                <LeaveApprovalRow
                  key={request.id}
                  request={request}
                  onApprove={handleApprove}
                  onReject={handleRejectClick}
                  disabled={processing}
                />
              ))}
            </Card>
          )}
        </TabsContent>

        {/* History */}
        <TabsContent value="history" className="mt-0 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <Select value={historyState} onValueChange={(value) => setHistoryState(value as HistoryState)}>
              <SelectTrigger className="h-9 w-[190px]">
                <SelectValue placeholder={t('leave.myRequests.filters.state', 'State')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('leave.myRequests.filters.allStates', 'All states')}</SelectItem>
                <SelectItem value="approved">{t('leave.state.approved', 'Approved')}</SelectItem>
                <SelectItem value="rejected">{t('leave.state.rejected', 'Rejected')}</SelectItem>
                <SelectItem value="cancelled">{t('leave.state.cancelled', 'Cancelled')}</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-xs text-muted-foreground tabular-nums">
              {t('leave.myRequests.filters.results', '{{count}} requests', { count: visibleHistory.length })}
            </span>
          </div>

          {historyLoading ? (
            <RowListSkeleton />
          ) : visibleHistory.length === 0 ? (
            <Card>
              <EmptyState
                icon={History}
                title={t('leave.pending.history.empty.title', 'Nessuna richiesta nello storico')}
                description={t(
                  'leave.pending.history.empty.description',
                  'Le richieste approvate, rifiutate o cancellate compariranno qui.',
                )}
              />
            </Card>
          ) : (
            <Card className="divide-y overflow-hidden">
              {visibleHistory.map((request) => (
                <LeaveApprovalRow key={request.id} request={request} variant="history" />
              ))}
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Reject Reason Dialog */}
      <RejectReasonDialog
        open={rejectDialogOpen}
        onClose={handleRejectDialogClose}
        onConfirm={handleRejectConfirm}
        entryCount={1}
        loading={processing}
      />
    </PageTransition>
  );
};

export default PendingLeaveRequests;
